import { Box, Paper, Typography } from "@mui/material";
import React from "react";
import { tokens } from "../../../theme";
import { onChrome } from "../appShell/shellChrome";

interface AuthLayoutProps {
  /** Show the navy brand panel. Login uses it; two-factor and reset do not. */
  brand?: boolean;
  children: React.ReactNode;
}

const HEADLINE = "We monitor key metrics to improve results";
const SUBCOPY =
  "Revenue, retention and forecast for every branch and department, refreshed nightly.";

/**
 * Frame for the signed-out screens — login, two-factor, password reset.
 *
 * `brand` splits the card: navy panel beside the form above md, and a short
 * banner above it below md, since a 400px panel beside a 375px viewport leaves
 * nothing for the form.
 */
const AuthLayout = ({ brand = false, children }: AuthLayoutProps) => (
  <Box
    sx={{
      // These screens own the whole viewport — App.tsx withholds the marketing
      // header and footer on the auth routes, so there is nothing above or
      // below to centre against.
      minHeight: "100vh",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      p: { xs: 2, md: 4 }
    }}
  >
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        width: "100%",
        maxWidth: brand ? 1000 : 460,
        overflow: "hidden",
        display: "flex",
        flexDirection: { xs: "column", md: "row" }
      }}
    >
      {brand && (
        <Box
          sx={{
            flex: "none",
            width: { xs: "100%", md: 400 },
            bgcolor: tokens.color.railBg,
            p: { xs: "24px", md: "36px 34px" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: { xs: 2, md: 4 }
          }}
        >
          <Box
            component="img"
            src={require("../../../assets/logo-mark.png")}
            alt="Zintoro"
            sx={{ height: { xs: 34, md: 46 }, alignSelf: "flex-start" }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
            <Typography
              sx={{
                fontSize: { xs: 20, md: 27 },
                fontWeight: 600,
                color: onChrome.strong,
                lineHeight: 1.28,
                letterSpacing: "-0.015em",
                textWrap: "pretty"
              }}
            >
              {HEADLINE}
            </Typography>
            <Typography
              sx={{
                display: { xs: "none", md: "block" },
                fontSize: tokens.typography.body,
                lineHeight: 1.6,
                color: "rgba(255,255,255,.66)",
                textWrap: "pretty"
              }}
            >
              {SUBCOPY}
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          p: { xs: "24px", md: brand ? "56px 52px" : "48px 40px" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: 2.75
        }}
      >
        {children}
      </Box>
    </Paper>
  </Box>
);

export default AuthLayout;
